/**
 * =============================================================================
 * TemplateJobDetailsDialog — acompanhamento detalhado de uma execução
 *
 * Reúne, em um único lugar: progresso, linha do tempo por módulo, erro geral
 * e as pendências deixadas pela execução. Reaproveita as consultas existentes
 * (`get_tenant_template_job` e `get_tenant_template_pending_actions`).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-08 v1.1.0 - Cabeçalho no padrão editorial (faixa escura, título
 *          único, botão de fechar branco), situação em bolinha colorida e barra de
 *          progresso fina exibida apenas enquanto a execução está em andamento.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (refatoração da tela).
 * =============================================================================
 */
import React from 'react';
import { Loader2, X } from 'lucide-react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { InlineNotice } from '@/components/ui/inline-notice';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { TemplateTimeline } from './TemplateTimeline';
import { useTemplateJob, useTemplatePendingActions } from '@/hooks/useSystemTemplates';
import { TEMPLATE_MODULE_CATALOG } from '@/lib/template-system/moduleCatalog';
import type { TemplateModuleKey } from '@/lib/template-system/constants';

/** Rótulos amigáveis das situações. */
const STATUS_LABEL: Record<string, string> = {
  pending: 'Na fila',
  running: 'Em andamento',
  paused: 'Pausado',
  completed: 'Concluído',
  failed: 'Com erro',
  compensating: 'Desfazendo',
  compensated: 'Desfeito',
};

/** Cor da bolinha de situação (mesmos tokens da Importação de Listas). */
const STATUS_DOT_COLORS: Record<string, string> = {
  pending: 'bg-muted-foreground',
  running: 'bg-blue-500',
  paused: 'bg-amber-500',
  completed: 'bg-green-500',
  failed: 'bg-destructive',
  compensating: 'bg-amber-500',
  compensated: 'bg-muted-foreground',
};


/** Rótulos amigáveis dos tipos de execução. */
const KIND_LABEL: Record<string, string> = {
  export: 'Geração do arquivo',
  import: 'Conferência do arquivo',
  apply: 'Aplicação nesta conta',
  rollback: 'Reversão',
};

interface TemplateJobDetailsDialogProps {
  jobId: string | null;
  onOpenChange: (aberto: boolean) => void;
  /** Recoloca a execução na fila (só aparece quando houve erro). */
  onRetry?: (jobId: string) => void;
  retrying?: boolean;
}

export function TemplateJobDetailsDialog({
  jobId,
  onOpenChange,
  onRetry,
  retrying,
}: TemplateJobDetailsDialogProps) {
  const { data: job, isLoading } = useTemplateJob(jobId);
  const { data: pendencias = [] } = useTemplatePendingActions(jobId);

  return (
    <Dialog open={!!jobId} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="max-w-3xl max-h-[85dvh] overflow-hidden flex flex-col gap-0 p-0"
      >
        {/* Cabeçalho no padrão editorial dos modais grandes do módulo */}
        <header className="relative shrink-0 bg-sidebar px-5 py-4 text-sidebar-foreground sm:px-7">
          <DialogTitle className="text-base font-semibold text-sidebar-foreground">
            {job ? KIND_LABEL[job.kind] ?? 'Execução' : 'Execução'}
          </DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm text-sidebar-foreground/70 transition-colors hover:text-sidebar-foreground">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </header>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {isLoading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando…
            </p>
          )}

          {job && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      STATUS_DOT_COLORS[job.status] || 'bg-muted-foreground',
                    )}
                  />
                  {STATUS_LABEL[job.status] ?? job.status}
                </div>
                <span className="text-xs text-muted-foreground">
                  Início: {job.started_at ? new Date(job.started_at).toLocaleString('pt-BR') : '—'}
                </span>
              </div>

              {/* Barra fina só enquanto está rodando; concluído mostra apenas o texto */}
              {(job.status === 'running' || job.status === 'compensating') && (
                <div className="flex items-center gap-3">
                  <Progress value={job.progress_percent} className="h-1.5 bg-muted" />
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {job.progress_percent ?? 0}%
                  </span>
                </div>
              )}

              {job.status === 'failed' && (
                <InlineNotice variant="error">
                  {(job.error_payload?.message as string) ?? 'A execução falhou.'}
                </InlineNotice>
              )}

              <div>
                <p className="mb-2 text-sm font-medium">Passos</p>
                <TemplateTimeline steps={job.steps ?? []} />
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Pendências</p>
                {pendencias.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma pendência registrada.</p>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Origem</TableHead>
                          <TableHead>Módulo</TableHead>
                          <TableHead>Descrição</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendencias.map((pendencia, indice) => (
                          <TableRow key={`${pendencia.origem}-${indice}`}>
                            <TableCell>{pendencia.origem}</TableCell>
                            <TableCell>
                              {pendencia.module_key
                                ? TEMPLATE_MODULE_CATALOG[pendencia.module_key as TemplateModuleKey]
                                    ?.name ?? pendencia.module_key
                                : '—'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {pendencia.descricao}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t px-6 py-3">
          {job?.status === 'failed' && onRetry && (
            <Button variant="outline" disabled={retrying} onClick={() => onRetry(job.id)}>
              {retrying && <Loader2 className="h-4 w-4 animate-spin" />}
              Tentar de novo
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateJobDetailsDialog;
